import { groupBy } from 'lodash';
import { merge, Observable, Subject } from 'rxjs';
import { tap, switchMap, take, takeUntil } from 'rxjs/operators';

import {
  Component,
  OnInit,
  OnDestroy,
  ViewContainerRef,
  EventEmitter,
  Output,
} from '@angular/core';

import { SnAbilityService } from '@lib/ability';

import { SnChannel } from '../../models';
import { SnChatService, SnChatDialogsService } from '../../services';

@Component({
  selector: 'sn-chat-channel-list',
  templateUrl: './chat-channel-list.container.html',
  styleUrls: ['./chat-channel-list.container.scss'],
  providers: [SnChatDialogsService],
})
export class SnChatChannelListContainer implements OnInit, OnDestroy {
  public teamChannels: SnChannel[] = [];
  public directChannels: SnChannel[] = [];
  public organizationChannels: SnChannel[] = [];
  public sharedChannels: SnChannel[] = [];
  public panelOpenState: boolean = false;
  public rememberChannel: any[] = [];

  public team: boolean = false;
  public channel: boolean = false;
  public direct: boolean = false;

  @Output()
  public readonly channelSelected: EventEmitter<SnChannel> = new EventEmitter();

  public readonly canAdd: boolean;
  public readonly canSee: boolean;

  private readonly _destroy$ = new Subject();

  constructor(
    private readonly _chatService: SnChatService,
    private readonly _chatDialogsService: SnChatDialogsService,
    private readonly _viewContainerRef: ViewContainerRef,
    ability: SnAbilityService,
  ) {
    this.canAdd = ability.can({ subject: 'communications/chatchannel', action: 'add' });
    this.canSee = ability.can({
      subject: 'users/user',
      action: 'view_communications_chatchannel_team',
    });
  }

  get activeChannel(): SnChannel {
    return this._chatService.activeChannel;
  }
  get isLoading(): boolean {
    return this._chatService.isLoading;
  }

  public ngOnInit(): void {
    this._groupChannels();

    merge(this._chatService.channelsLoaded$, this._chatService.channelsUpdated$)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._groupChannels();
      });

    this.fetchChannelsAreOpen();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onChannelSelected(channel: SnChannel): void {
    this.channelSelected.emit(channel);
    this._chatService.setActiveChannel(channel.sid);
  }

  public addOrganizationChannel(): void {
    this._chatDialogsService
      .openChannelFormDialog({
        viewContainerRef: this._viewContainerRef,
        data: {
          type: 'organization',
          userId: this._chatService.userId,
        },
      })
      .pipe(
        switchMap((channel: SnChannel) => this._setActiveChannelAfterUpdate(channel)),
        takeUntil(this._destroy$),
      )
      .subscribe();
  }

  public addDirectChannel(): void {
    this._chatDialogsService
      .openChannelFormDialog({
        viewContainerRef: this._viewContainerRef,
        data: {
          type: 'direct',
          userId: this._chatService.userId,
        },
      })
      .pipe(
        tap((channel: SnChannel) => {
          if (this._chatService.channels.has(channel.id)) {
            this._chatService.setActiveChannel(channel.sid);
          } else {
            this._setActiveChannelAfterUpdate(channel).pipe(takeUntil(this._destroy$)).subscribe();
          }
        }),
        takeUntil(this._destroy$),
      )
      .subscribe();
  }

  public expansionPanelEventOpen(channel: string): void {
    const currentPannel: any = {};
    switch (channel) {
      case 'team':
        currentPannel.team = this.team;
        break;
      case 'channels':
        currentPannel.channels = this.channel;
        break;
      case 'direct':
        currentPannel.direct = this.direct;
        break;
    }

    let setChannel = [];
    if (localStorage.getItem('setChannel')) {
      setChannel = JSON.parse(localStorage.getItem('setChannel'));
      if (!currentPannel[channel]) {
        const newChannel = [...setChannel, channel];
        localStorage.setItem('setChannel', JSON.stringify(newChannel));
      }
    } else {
      localStorage.setItem('setChannel', JSON.stringify([channel]));
    }
  }

  public expansionPanelEventClose(channel: string): void {
    let setChannel = [];
    setChannel = JSON.parse(localStorage.getItem('setChannel'));
    if (setChannel.includes(channel)) {
      const newChannel = setChannel.filter((item) => item !== channel);
      localStorage.setItem('setChannel', JSON.stringify(newChannel));
    }
  }

  public fetchChannelsAreOpen(): void {
    const channels: any[] = localStorage.getItem('setChannel')
      ? JSON.parse(localStorage.getItem('setChannel'))
      : [];
    this.team = channels.includes('team') ? true : false;
    this.channel = channels.includes('channels') ? true : false;
    this.direct = channels.includes('direct') ? true : false;
  }

  private _groupChannels(): void {
    const channels = this._chatService.channels;
    const grouppedChannels = groupBy(channels, (channel) => {
      if (channel.isUserChannel || channel.isPairChannel) {
        return 'direct';
      } else if (channel.isOrganizationChannel && !channel.hasExternalMembers) {
        return 'organization';
      } else if (channel.isOrganizationChannel && channel.hasExternalMembers) {
        return 'external';
      } else if (channel.isTeamChannel) {
        return 'team';
      }
    });

    this.teamChannels = grouppedChannels.team || [];
    this.directChannels = grouppedChannels.direct || [];
    this.organizationChannels = grouppedChannels.organization || [];
    this.sharedChannels = grouppedChannels.external || [];
  }

  private _setActiveChannelAfterUpdate(channel: SnChannel): Observable<void> {
    return this._chatService.channelsUpdated$.pipe(
      take(1),
      tap(() => this._chatService.setActiveChannel(channel.sid)),
    );
  }
}
